import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, ShoppingBag, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: "", type: "digital" });
  const [loadingCheckout, setLoadingCheckout] = useState(null);

  const load = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...form, price: parseFloat(form.price) });
      toast.success("Product created");
      setShow(false);
      setForm({ name: "", description: "", price: "", type: "digital" });
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const buy = async (p) => {
    setLoadingCheckout(p.id);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: `product_${p.id}`,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoadingCheckout(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Products & Smart Checkout</h1>
          <p className="text-sm text-zinc-500 mt-1">Sell digital products, coaching, and courses. AI attaches payment links to conversations.</p>
        </div>
        <button onClick={() => setShow(true)} className="rounded-full ashra-gradient-bg px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="add-product-btn">
          <Plus className="h-4 w-4" /> New product
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="rounded-3xl border border-black/5 bg-white border border-black/5 shadow-sm p-6" data-testid={`product-${p.id}`}>
            <div className="h-10 w-10 rounded-xl bg-violet-500/15 text-violet-300 grid place-items-center mb-3"><ShoppingBag className="h-4 w-4" /></div>
            <h3 className="font-display text-xl font-bold">{p.name}</h3>
            <p className="text-sm text-zinc-500 mt-1 min-h-[40px]">{p.description}</p>
            <p className="mt-4 font-display text-3xl font-extrabold">${p.price}</p>
            <p className="text-xs text-zinc-500 capitalize">{p.type}</p>
            <button
              onClick={() => buy(p)}
              disabled={loadingCheckout === p.id}
              className="mt-5 w-full rounded-full bg-white text-zinc-950 font-medium py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid={`checkout-${p.id}`}
            >
              <Zap className="h-4 w-4" /> {loadingCheckout === p.id ? "Opening Stripe…" : "Test smart checkout"}
            </button>
          </div>
        ))}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="w-full max-w-md rounded-3xl border border-black/5 bg-zinc-950 p-6" data-testid="add-product-modal">
            <h3 className="font-display text-xl font-bold mb-4">Create product</h3>
            <div className="space-y-2">
              <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Name" className="w-full rounded-xl bg-white border border-black/5 shadow-sm px-3 py-2 text-sm" />
              <textarea required value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Description" className="w-full rounded-xl bg-white border border-black/5 shadow-sm px-3 py-2 text-sm" />
              <input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} placeholder="Price (USD)" className="w-full rounded-xl bg-white border border-black/5 shadow-sm px-3 py-2 text-sm" />
              <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="w-full rounded-xl bg-white border border-black/5 shadow-sm px-3 py-2 text-sm">
                <option value="digital">Digital</option>
                <option value="course">Course</option>
                <option value="coaching">Coaching</option>
                <option value="subscription">Subscription</option>
              </select>
            </div>
            <button type="submit" className="mt-4 w-full rounded-full ashra-gradient-bg py-2 text-sm font-medium">Create</button>
          </form>
        </div>
      )}
    </div>
  );
}
